import { useSiteText } from "../i18n";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navigation } from "../data/navigation";
import { BrandMark, Icon } from "./Icon";
import { ButtonLink } from "./UI";

export function Navigation() {
  const txt = useSiteText();
  const [open, setOpen] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);
  const header = useRef<HTMLElement>(null);
  const location = useLocation();
  useEffect(() => {
    setOpen(null);
    setMobile(false);
  }, [location]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) {
        setOpen(null);
        setMobile(false);
      }
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, []);
  const closeWithKeyboard = (event: React.KeyboardEvent) => {
    if (event.key !== "Escape") return;
    if (open) {
      header.current
        ?.querySelector<HTMLButtonElement>(`[data-group="${open}"]`)
        ?.focus();
      setOpen(null);
    } else {
      setMobile(false);
      header.current
        ?.querySelector<HTMLButtonElement>(".mobile-toggle")
        ?.focus();
    }
  };
  return (
    <header
      className="site-header"
      ref={header}
      onKeyDown={closeWithKeyboard}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setOpen(null);
          setMobile(false);
        }
      }}
    >
      <div className="nav-shell container">
        <Link to="/" className="brand" aria-label={txt("Systein home")}>
          <BrandMark />
          <span>
            {txt("systein")}
            <span className="brand-period">.</span>
          </span>
        </Link>
        <button
          className="mobile-toggle"
          type="button"
          aria-label={txt(mobile ? "Close navigation" : "Open navigation")}
          aria-expanded={mobile}
          aria-controls="primary-navigation"
          onClick={() => {
            setMobile(!mobile);
            setOpen(null);
          }}
        >
          <Icon name={mobile ? "close" : "menu"} />
        </button>
        <nav
          id="primary-navigation"
          aria-label={txt("Main navigation")}
          className={`primary-navigation ${mobile ? "is-mobile-open" : ""}`}
        >
          {navigation.map((group) => (
            <div className="nav-group" key={group.label}>
              {group.items ? (
                <>
                  <button
                    type="button"
                    className={`nav-trigger ${open === group.label ? "is-open" : ""}`}
                    data-group={group.label}
                    aria-expanded={open === group.label}
                    aria-controls={`menu-${group.label.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() =>
                      setOpen(open === group.label ? null : group.label)
                    }
                  >
                    {txt(group.label)}
                    <Icon name="chevron" size={13} />
                  </button>
                  <div
                    id={`menu-${group.label.toLowerCase().replace(/ /g, "-")}`}
                    className="mega-menu"
                    hidden={open !== group.label}
                  >
                    <div className="mega-intro">
                      <p className="eyebrow">{txt(group.label)}</p>
                      <p>{txt(group.intro)}</p>
                      <span className="mega-line" />
                    </div>
                    <div className="mega-links">
                      {group.items.map((item) => (
                        <Link key={item.to} to={item.to}>
                          <span>
                            {txt(item.label)}
                            <Icon name="arrow" size={17} />
                          </span>
                          <small>{txt(item.description)}</small>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <Link className="nav-trigger" to={group.to || "/"}>
                  {txt(group.label)}
                </Link>
              )}
            </div>
          ))}
          <ButtonLink
            to="/contact?interest=demo"
            className="nav-cta"
            arrow={false}
          >
            {txt("Book a Demo")}
          </ButtonLink>
        </nav>
      </div>
    </header>
  );
}
